import { format, subYears, subSeconds, addMonths, addYears } from 'date-fns';
import nb from 'date-fns/locale/nb';
import {
    getFirstDayOfMonth,
    formatUTCDate,
    formatUTCDateTime,
} from '@klp/utils/dateTimeFormatter';

import { ExpiryType } from './ExpiryType';

export interface IDateRange {
    readonly fromDate: Date;
    readonly toDate: Date;
    readonly toDateNextMonth: Date;
}

export class ContractLinesDateRange implements IDateRange {
    public static parseObject(obj: IDateRange): ContractLinesDateRange {
        try {
            return new ContractLinesDateRange(obj);
        } catch (error) {
            throw new Error(
                `Could not parse ContractLinesDateRange object : ${JSON.stringify(
                    obj,
                )}`,
            );
        }
    }

    public static firstDayOfMonth = getFirstDayOfMonth();

    public static firstDayOfMonthFormatted = () =>
        format(getFirstDayOfMonth(), 'd. MMMM yyyy', { locale: nb });
    public static firstDayOfMonthAPIFormatted = () =>
        formatUTCDate(getFirstDayOfMonth());
    public static firstDayOfMonthLocaleFormatted = () =>
        format(getFirstDayOfMonth(), 'dd.MM.yyyy');

    public static toDateNoYearFormatted = () =>
        format(getFirstDayOfMonth(), 'd. MMMM', { locale: nb });

    public static fromExpiryType(
        expiry: ExpiryType,
        firstDayOfMonth = ContractLinesDateRange.firstDayOfMonth,
    ): ContractLinesDateRange {
        switch (expiry) {
            case ExpiryType.list.expired: {
                return this.parseObject({
                    fromDate: subYears(firstDayOfMonth, 100),
                    toDate: subSeconds(firstDayOfMonth, 1),
                    toDateNextMonth: firstDayOfMonth,
                });
            }
            case ExpiryType.list.expires_within_6m: {
                const sixMonthsLater = addMonths(firstDayOfMonth, 6);
                return this.parseObject({
                    fromDate: firstDayOfMonth,
                    toDate: subSeconds(sixMonthsLater, 1),
                    toDateNextMonth: sixMonthsLater,
                });
            }
            case ExpiryType.list.expires_within_1y: {
                const oneYearLater = addYears(firstDayOfMonth, 1);
                return this.parseObject({
                    fromDate: firstDayOfMonth,
                    toDate: subSeconds(oneYearLater, 1),
                    toDateNextMonth: oneYearLater,
                });
            }
            case ExpiryType.list.expires_within_2y: {
                const twoYearsLater = addYears(firstDayOfMonth, 2);
                return this.parseObject({
                    fromDate: firstDayOfMonth,
                    toDate: subSeconds(twoYearsLater, 1),
                    toDateNextMonth: twoYearsLater,
                });
            }
            case ExpiryType.list.expires_within_3y: {
                const threeYearsLater = addYears(firstDayOfMonth, 3);
                return this.parseObject({
                    fromDate: firstDayOfMonth,
                    toDate: subSeconds(threeYearsLater, 1),
                    toDateNextMonth: threeYearsLater,
                });
            }
            case ExpiryType.list.expires_within_4y: {
                const fourYearsLater = addYears(firstDayOfMonth, 4);
                return this.parseObject({
                    fromDate: firstDayOfMonth,
                    toDate: subSeconds(fourYearsLater, 1),
                    toDateNextMonth: fourYearsLater,
                });
            }
            case ExpiryType.list.eternal: {
                return this.parseObject({
                    fromDate: subYears(firstDayOfMonth, 100),
                    toDate: addYears(firstDayOfMonth, 100),
                    toDateNextMonth: firstDayOfMonth,
                });
            }
            default:
                throw new Error('Unknown expiry type: ' + expiry);
        }
    }

    readonly fromDate: IDateRange['fromDate'];
    readonly toDate: IDateRange['toDate'];
    readonly toDateNextMonth: IDateRange['toDateNextMonth'];

    constructor(props: IDateRange) {
        Object.assign(this, props);
    }

    public fromDateFormatted = () => formatUTCDateTime(this.fromDate, 'start');
    public toDateFormatted = () => formatUTCDateTime(this.toDate, 'end');
    public toDateYearMonthDateFormatted = () => formatUTCDate(this.toDate);
    public toDateNoTimeFormatted = () =>
        format(this.toDate, 'd. MMMM yyyy', { locale: nb });
    public toDateNoYearFormatted = () =>
        format(this.toDate, 'd. MMMM', { locale: nb });
    public toDateNextMonthLocaleFormatted = () =>
        format(this.toDateNextMonth, 'dd.MM.yyyy');
}
